const _MS_PER_DAY = 1000 * 60 * 60 * 24;

const _UTC_FALLBACK = 'UTC';

export function getIanaTimeZoneId(): string {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (
      timeZone &&
      typeof timeZone === 'string' &&
      timeZone.trim().length > 0
    ) {
      return timeZone;
    }
  } catch {
    // Intl not available or resolvedOptions().timeZone threw
  }
  return _UTC_FALLBACK;
}

export function formatLongDate(date: Date, ianaTimeZone?: string) {
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(ianaTimeZone && { timeZone: ianaTimeZone }),
  });
}

export function formatDayDate(date: Date, ianaTimeZone?: string) {
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(ianaTimeZone && { timeZone: ianaTimeZone }),
  });
}

export function formatMonthDate(date: Date, ianaTimeZone?: string) {
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    ...(ianaTimeZone && { timeZone: ianaTimeZone }),
  });
}

export function formatSortDate(date: Date, ianaTimeZone?: string) {
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(ianaTimeZone && { timeZone: ianaTimeZone }),
  });
}

export function formatSortDayDate(date: Date, ianaTimeZone?: string) {
  return date.toLocaleDateString('es-ES', {
    month: '2-digit',
    day: '2-digit',
    ...(ianaTimeZone && { timeZone: ianaTimeZone }),
  });
}

export function formatLongHourDate(date: Date, ianaTimeZone?: string) {
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...(ianaTimeZone && { timeZone: ianaTimeZone }),
  });
}

export function formatHourDate(date: Date, ianaTimeZone?: string) {
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...(ianaTimeZone && { timeZone: ianaTimeZone }),
  });
}

/**
 * Formats a duration in seconds as `mm:ss`, or `hh:mm:ss` once it reaches an hour.
 * The seconds are floored because durations measured by the API are fractional.
 */
export function formatTimestamp(timestamp: number) {
  const h = Math.floor(timestamp / 3600);
  const m = Math.floor(timestamp / 60) % 60;
  const s = Math.floor(timestamp % 60);

  return [h, m, s]
    .map((v) => (v < 10 ? '0' + v : v))
    .filter((v, i) => v !== '00' || i > 0)
    .join(':');
}

export function timeFromSeconds(secs: number) {
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor(secs / 60) % 60;
  const seconds = Math.floor(secs % 60);

  return [hours, minutes, seconds];
}

export function formatFromSeconds(secs: number) {
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor(secs / 60) % 60;
  const seconds = Math.floor(secs % 60);

  if (hours > 0) {
    return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  }

  return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
}

export function dateDiffInDays(a: Date, b: Date) {
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export function dateDiffInMinutes(a: Date, b: Date) {
  const utc1 = Date.UTC(
    a.getFullYear(),
    a.getMonth(),
    a.getDate(),
    a.getHours(),
    a.getMinutes(),
  );
  const utc2 = Date.UTC(
    b.getFullYear(),
    b.getMonth(),
    b.getDate(),
    b.getHours(),
    b.getMinutes(),
  );

  return Math.floor((utc2 - utc1) / (1000 * 60));
}

export function dateDiffInSeconds(a: Date, b: Date) {
  const utc1 = Date.UTC(
    a.getFullYear(),
    a.getMonth(),
    a.getDate(),
    a.getHours(),
    a.getMinutes(),
    a.getSeconds(),
  );
  const utc2 = Date.UTC(
    b.getFullYear(),
    b.getMonth(),
    b.getDate(),
    b.getHours(),
    b.getMinutes(),
    b.getSeconds(),
  );

  return Math.floor((utc2 - utc1) / 1000);
}

/**
 * The window the attempt lists query over: from a number of months back, to the end of
 * today, so an attempt started earlier today is always inside it.
 */
export function lastMonthsRange(months: number): [Date, Date] {
  return [
    new Date(new Date().setMonth(new Date().getMonth() - months)),
    new Date(new Date().setHours(23, 59, 59, 999)),
  ];
}

export function addSeconds(date: Date, seconds: number) {
  date.setTime(date.getTime() + seconds * 1000);
  return date;
}

export function addHours(date: Date, hours: number) {
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);
  return date;
}

export function subtractHours(date: Date, hours: number) {
  date.setTime(date.getTime() - hours * 60 * 60 * 1000);
  return date;
}

export function getUTCTimezoneOffset(date: Date) {
  const currentTimezone = date.getTimezoneOffset();
  const { hours, minutes } = toHoursAndMinutes(currentTimezone * -1);

  let gmt = 'UTC';
  gmt += currentTimezone === 0 ? ' ' : currentTimezone > 0 ? ' -' : ' +';
  gmt += `${hours < 10 ? '0' : ''}${hours}:${
    minutes < 10 ? '0' : ''
  }${minutes}`;

  return gmt;
}

export function toHoursAndMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return { hours, minutes };
}

/** The user's local calendar date as YYYY-MM-DD, for file names. */
export function formatFileDate(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}
